import type { As } from '@chakra-ui/react';
import { Box, Flex, Tooltip, chakra, VStack } from '@chakra-ui/react';
import React from 'react';

import type { AddressParam } from 'types/api/addressParams';

import { route } from 'nextjs-routes';

import { getEnvValue } from 'configs/app/utils';
import { toBech32Address } from 'lib/address/bech32';
import { useQnsResolutionFull } from 'lib/api/useQnsResolution';
import { useAddressHighlightContext } from 'lib/contexts/addressHighlight';
import { useSettingsContext } from 'lib/contexts/settings';
import Skeleton from 'ui/shared/chakra/Skeleton';
import * as EntityBase from 'ui/shared/entities/base/components';
import QnsDomainsTooltipContent from 'ui/shared/qns/QnsDomainsTooltipContent';
import QnsIcon from 'ui/shared/qns/QnsIcon';

import { distributeEntityProps, getIconProps } from '../base/utils';
import AddressEntityContentProxy from './AddressEntityContentProxy';
import AddressIconDelegated from './AddressIconDelegated';
import AddressIdenticon from './AddressIdenticon';

// Phase 3 feature flags — each can be killed independently of the others
// and independently of Phase 1/2 by flipping to "false" in envs.js (no rebuild).
const QNS_ICON_ENABLED = getEnvValue('NEXT_PUBLIC_QNS_ICON_ENABLED') !== 'false';
const QNS_TOOLTIP_ENABLED = getEnvValue('NEXT_PUBLIC_QNS_TOOLTIP_ENABLED') !== 'false';

type LinkProps = EntityBase.LinkBaseProps & Pick<EntityProps, 'address'>;

const getDisplayedAddress = (address: AddressProp, altHash?: string) => {
  return address.filecoin?.robust ?? address.filecoin?.id ?? altHash ?? address.hash;
};

const Link = chakra((props: LinkProps) => {
  const defaultHref = route({ pathname: '/address/[hash]', query: { ...props.query, hash: props.address.hash } });

  return (
    <EntityBase.Link
      { ...props }
      href={ props.href ?? defaultHref }
    >
      { props.children }
    </EntityBase.Link>
  );
});

type IconProps = Pick<EntityProps, 'address' | 'isSafeAddress'> & EntityBase.IconBaseProps;

const Icon = (props: IconProps) => {
  if (props.noIcon) {
    return null;
  }

  const styles = {
    ...getIconProps(props.size),
    marginRight: props.marginRight ?? 2,
  };

  if (props.isLoading) {
    return <Skeleton { ...styles } borderRadius="full" flexShrink={ 0 }/>;
  }

  const isDelegatedAddress = props.address.proxy_type === 'eip7702';

  if (props.address.is_contract && !isDelegatedAddress) {
    if (props.isSafeAddress) {
      return (
        <EntityBase.Icon
          { ...props }
          name="brands/safe"
        />
      );
    }

    const isProxy = Boolean(props.address.implementations?.length);
    const isVerified = isProxy ? props.address.is_verified && props.address.implementations?.every(({ name }) => Boolean(name)) : props.address.is_verified;
    const contractIconName: EntityBase.IconBaseProps['name'] = props.address.is_verified ? 'contracts/verified' : 'contracts/regular';
    const label = (isVerified ? 'verified ' : '') + (isProxy ? 'proxy contract' : 'contract');

    return (
      <Tooltip label={ label.slice(0, 1).toUpperCase() + label.slice(1) }>
        <span>
          <EntityBase.Icon
            { ...props }
            name={ isProxy ? 'contracts/proxy' : contractIconName }
            color={ isVerified ? 'green.500' : undefined }
            borderRadius={ 0 }
          />
        </span>
      </Tooltip>
    );
  }

  const label = (() => {
    if (isDelegatedAddress) {
      return props.address.is_verified ? 'EOA + verified code' : 'EOA + code';
    }
  })();

  return (
    <Tooltip label={ label }>
      <Flex marginRight={ styles.marginRight } position="relative">
        <AddressIdenticon
          size={ props.size === 'lg' ? 30 : 20 }
          hash={ getDisplayedAddress(props.address) }
        />
        { isDelegatedAddress && <AddressIconDelegated isVerified={ Boolean(props.address.is_verified) }/> }
      </Flex>
    </Tooltip>
  );
};

export type ContentProps = Omit<EntityBase.ContentBaseProps, 'text'> & Pick<EntityProps, 'address'> & { altHash?: string };

const Content = chakra((props: ContentProps) => {
  const displayedAddress = getDisplayedAddress(props.address, props.altHash);
  const nameTag = props.address.metadata?.tags.find(tag => tag.tagType === 'name')?.name;
  // QNS lookup — returns null when feature is disabled, address has no .qie
  // domain, or the lookup fails. In all those cases the fallback chain
  // collapses to exactly the original behaviour.
  //
  // Phase 3: switched from useQnsResolution() to useQnsResolutionFull() so we
  // can show an icon when the displayed name came from QNS, and enrich the
  // tooltip when the address owns multiple .qie domains. Both hooks share the
  // same React Query cache key under the hood — no extra network calls.
  const qnsResolution = useQnsResolutionFull(props.address.hash);
  const qnsName = qnsResolution?.primaryDomain ?? null;
  const nameText = nameTag || qnsName || props.address.ens_domain_name || props.address.name;

  // Phase 3 — icon: only shown when the *displayed* name actually came from
  // QNS (i.e., nameTag and ens_domain_name did NOT supersede qnsName) AND
  // the feature flag is on. This prevents a confusing icon showing next to
  // an ENS name or a custom name tag.
  const showQnsIcon = QNS_ICON_ENABLED && Boolean(qnsName) && nameText === qnsName && !nameTag;

  // Phase 3 — multi-domain tooltip: only enriched when the address has more
  // than one .qie domain. For exactly-one domain the existing tooltip already
  // shows everything useful.
  const showMultipleDomains = QNS_TOOLTIP_ENABLED &&
    qnsResolution !== null &&
    qnsResolution.totalDomains > 1 &&
    Boolean(qnsName) &&
    nameText === qnsName &&
    !nameTag;

  const isProxy = props.address.implementations && props.address.implementations.length > 0 && props.address.proxy_type !== 'eip7702';

  if (isProxy) {
    return <AddressEntityContentProxy { ...props }/>;
  }

  if (nameText) {
    const label = (
      <VStack gap={ 0 } py={ 1 } color="inherit" align="stretch">
        <Box fontWeight={ 600 } whiteSpace="pre-wrap" wordBreak="break-word">{ nameText }</Box>
        <Box whiteSpace="pre-wrap" wordBreak="break-word">
          { displayedAddress }
        </Box>
        { showMultipleDomains && qnsResolution && (
          <Box mt={ 2 } pt={ 2 } borderTopWidth="1px" borderTopColor="whiteAlpha.300">
            <QnsDomainsTooltipContent
              address={ props.address.hash }
              primaryDomain={ qnsResolution.primaryDomain }
              allDomains={ qnsResolution.allDomains }
              totalDomains={ qnsResolution.totalDomains }
            />
          </Box>
        ) }
      </VStack>
    );

    return (
      <Tooltip label={ label } maxW={{ base: 'calc(100vw - 8px)', lg: '400px' }}>
        <Skeleton isLoaded={ !props.isLoading } overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" as="span">
          { showQnsIcon && (
            <chakra.span display="inline-flex" alignItems="center" verticalAlign="middle" mr={ 1 }>
              <QnsIcon boxSize={ 4 }/>
            </chakra.span>
          ) }
          { nameText }
        </Skeleton>
      </Tooltip>
    );
  }

  return (
    <EntityBase.Content
      { ...props }
      text={ displayedAddress }
    />
  );
});

type CopyProps = Omit<EntityBase.CopyBaseProps, 'text'> & Pick<EntityProps, 'address'> & { altHash?: string };

const Copy = (props: CopyProps) => {
  return (
    <EntityBase.Copy
      { ...props }
      text={ getDisplayedAddress(props.address, props.altHash) }
    />
  );
};

const Container = EntityBase.Container;

interface AddressProp extends Partial<AddressParam> {
  hash: string;
}

export interface EntityProps extends EntityBase.EntityBaseProps {
  address: AddressProp;
  isSafeAddress?: boolean;
  noHighlight?: boolean;
  noAltHash?: boolean;
}

const AddressEntry = (props: EntityProps) => {
  const partsProps = distributeEntityProps(props);
  const highlightContext = useAddressHighlightContext(props.noHighlight);
  const settingsContext = useSettingsContext();
  const altHash = !props.noAltHash && settingsContext?.addressFormat === 'bech32' ? toBech32Address(props.address.hash) : undefined;

  return (
    <Container
      // we have to use the global classnames here, see theme/global.ts
      // otherwise, if we use sx prop, Chakra will generate the same styles for each instance of the component on the page
      className={ `${ props.className } address-entity ${ props.noCopy ? 'address-entity_no-copy' : '' }` }
      data-hash={ highlightContext && !props.isLoading ? props.address.hash : undefined }
      onMouseEnter={ highlightContext?.onMouseEnter }
      onMouseLeave={ highlightContext?.onMouseLeave }
      position="relative"
      zIndex={ 0 }
    >
      <Icon { ...partsProps.icon }/>
      <Link { ...partsProps.link }>
        <Content { ...partsProps.content } altHash={ altHash }/>
      </Link>
      <Copy { ...partsProps.copy } altHash={ altHash }/>
    </Container>
  );
};

export default React.memo(chakra<As, EntityProps>(AddressEntry));

export {
  Container,
  Link,
  Icon,
  Content,
  Copy,
};